// screens/OrderTrackingScreen.tsx
import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Linking,
} from "react-native";
import { Image } from "react-native";
import {
  Phone,
  MapPin,
  User,
  ArrowLeft,
  MessageCircle,
} from "lucide-react-native";
import { menuScreenStyles as styles } from "../components/shared/styles/styles";
import { registrationStyles } from "../components/shared/styles/registrationStyles";
import { FlickOColors } from "../components/shared/styles/color";
import { getResponsiveSize } from "../components/shared/styles/globalStyles";
import { RootStackParamList } from "../types/navigation";
import { NavigationProp, RouteProp } from "@react-navigation/native";
import { homeStyles } from "../components/shared/styles/homeStyles";
import BackButton from "../components/shared/component/BackButton";
import HeaderBack from "../components/shared/component/HeaderBack";

interface OrderTrackingScreenProps {
  route: RouteProp<RootStackParamList, "OrderTrackingScreen">;
  navigation: NavigationProp<RootStackParamList>;
}
export interface OrderDetails {
  orderId: string;
  estimatedTime: string;
  restaurant: {
    name: string;
    phone: string;
  };
  delivery: {
    name: string;
    phone: string;
    address: string;
  };
}

const OrderHeader = ({ orderId, estimatedTime, navigation }) => (
  <View
    style={[
      homeStyles.topBar,
      { paddingVertical: getResponsiveSize(0), paddingLeft: 0 },
    ]}
  >
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <HeaderBack />
      <View>
        <Text style={styles.headerTitle}>Order {orderId}</Text>
        <Text style={homeStyles.locationSubtitle}>
          Arriving in {estimatedTime}
        </Text>
      </View>
    </View>
  </View>
);

const ContactCard = ({ title, name, image, phone = true, phoneNumber }) => (
  <View style={[registrationStyles.card, registrationStyles.cardMargin]}>
    <Text style={styles.billTitle}>{title}</Text>
    <View style={[styles.billRow, { alignItems: "center" }]}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
          gap: getResponsiveSize(10),
        }}
      >
        <Image
          source={
            image?.length > 0
              ? { uri: image }
              : title === "Restaurant"
              ? require("../../assets/defautlRestaurant.jpg")
              : require("../../assets/delGuy.png")
          }
          style={{
            width: getResponsiveSize(60),
            height: getResponsiveSize(60),
            borderRadius: getResponsiveSize(30),
          }}
        />
        <Text
          numberOfLines={2}
          ellipsizeMode="tail"
          style={[
            styles.itemName,
            {
              flex: 1,
            },
          ]}
        >
          {name}
        </Text>
      </View>
      {phone && (
        <View style={{ flexDirection: "row", gap: getResponsiveSize(16) }}>
          <TouchableOpacity
            onPress={() => phoneNumber && Linking.openURL(`tel:${phoneNumber}`)}
          >
            <Phone size={24} color={FlickOColors.primary} />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() =>
              phoneNumber &&
              Linking.openURL(`whatsapp://send?phone=${phoneNumber}`)
            }
          >
            <MessageCircle size={24} color={FlickOColors.success} />
          </TouchableOpacity>
        </View>
      )}
    </View>
  </View>
);

const PaymentNoticeCard = ({ amount }) => (
  <View style={[registrationStyles.card, registrationStyles.cardMargin]}>
    <Text style={styles.itemName}>Pay ₹{amount} on delivery</Text>
    <Text style={styles.itemDescription}>
      Please keep a change of ₹{amount} handy or pay using UPI to delivery
      partner
    </Text>
    <View style={styles.upiContainer}>
      <Image
        source={require("../../assets/upi.png")}
        style={{
          width: getResponsiveSize(120),
          height: getResponsiveSize(48),
          resizeMode: "contain",
          marginLeft: 0,
        }}
      />
    </View>
  </View>
);

const AddressCard = ({ address }: { address: string }) => (
  <View style={[registrationStyles.card, registrationStyles.cardMargin]}>
    <Text style={styles.billTitle}>Delivery address</Text>
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      <MapPin size={20} color={FlickOColors.primary} />
      <Text style={styles.itemDescription}>{address}</Text>
    </View>
  </View>
);

const OrderTrackingScreen = ({
  route,
  navigation,
}: OrderTrackingScreenProps) => {
  const { orderDetails } = route.params;

  if (!orderDetails) return null;

  return (
    <View style={styles.container}>
      <OrderHeader
        orderId={orderDetails.orderId}
        estimatedTime={orderDetails.estimatedTime}
        navigation={navigation}
      />

      <ScrollView>
        <ContactCard
          title="Restaurant"
          name={`${orderDetails.restaurant.name} - has received your order`}
          image={orderDetails.restaurant.image}
          phoneNumber={orderDetails.delivery.phone}
        />

        <ContactCard
          title="Delivery Partner"
          name={orderDetails.delivery.name}
          image={orderDetails.delivery.image}
          phoneNumber={orderDetails.delivery.phone}
        />

        <PaymentNoticeCard amount={257} />

        <AddressCard address={orderDetails.delivery.address} />

        <View style={[registrationStyles.card, registrationStyles.cardMargin]}>
          <TouchableOpacity>
            <Text style={styles.billTitle}>Need help with your order?</Text>
            <Text
              style={[styles.itemDescription, { color: FlickOColors.primary }]}
            >
              Get help & support
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

export default OrderTrackingScreen;
